import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Dropdown, Flex, Modal, Popover, Space, Typography, notification } from 'antd'
import { Header } from 'antd/es/layout/layout'
import { LeftOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import { FavoriteStarIcon, ArrowDownIcon, HelpIcon, HomeSVG, DangerIcon } from 'src/assets/icons'
import ProfilePic from 'src/assets/images/profile-pic.png'
import { colorTextSubtle } from 'src/app/themes/primaryThemeDefault'
import { menuItems, menuUrls, UrlToPageNameMapping } from 'src/app/shared/constants/menu.constants'
import { IUserPage } from 'src/app/shared/models/userPages.model'
import {
  defaultHomePage,
  ROUTES_WITH_BACK_BUTTON,
  ROUTES_HIDDEN_FAVORITE_ICON,
  ROUTES_HIDDEN_HELP_ICON,
} from 'src/app/shared/constants/app.constants'
import {
  addPageToFavorites,
  getAllPages,
  getFavoritePages,
  removePageFromFavorites,
  updateFavoritePages,
} from 'src/app/shared/services/UserPagesService'
import { getSelectedPageKey, setSelectedPageKey } from 'src/app/store/common'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { sortByOrder } from 'src/app/shared/utils'
import { getUserProfile } from 'src/app/store/auth'
import DNDComp from './favorites/favorite-confirmation-modal/FavoriteConfirmationModal'
import FavoriteList from './favorites/FavoriteList'
import AccountMenu from './account-menu'
import './index.scss'
import { PREVIEW_HELP_PAGE, TEMP_SESSION_TRANSFER } from '../../../../constants/localStorage.constants'
import { getCriticalConfig } from 'src/app/store/masterSetup'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'
import { MinimizeIconSVG } from 'src/assets/icons'

const AppHeader: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { mode } = useWindowSize()
  const criticalConfig = useSelector(getCriticalConfig)
  const selectedPageKey = useSelector(getSelectedPageKey)
  const userProfile = useSelector(getUserProfile)
  const { userId, name, policies } = userProfile
  const params = useParams()
  const { id } = params

  const [api, contextHolder] = notification.useNotification()
  const [currentPage, setCurrentPage] = useState<any>(null)
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const [isFavoriteConfirmationModalOpen, setIsFavoriteConfirmationModalOpen] = useState(false)
  const [isUnsaveAllFavoritePagesModalOpen, setIsUnsaveAllFavoritePagesModalOpen] = useState(false)
  const [isFavoriteIconDisabled, setIsFavoriteIconDisabled] = useState<boolean>(false)
  const [allPagesList, setAllPagesList] = useState([])
  const [favoritePagesList, setFavoritePagesList] = useState<any>([])
  const [initialAllPagesList, setInitialAllPagesList] = useState([])
  const [initialFavoritePages, setInitialFavoritePages] = useState([])
  const [flag, setFlag] = useState(false)
  const [reget, setReget] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const pathName = useMemo(() => {
    if (id) return location.pathname?.replace(`/${id}`, '')

    return location.pathname
  }, [id, location])

  const isFavoriteIconDisplay = !ROUTES_HIDDEN_FAVORITE_ICON?.includes(pathName as menuUrls)
  const isHelpIconDisplay = !ROUTES_HIDDEN_HELP_ICON?.includes(pathName as menuUrls)

  useEffect(() => {
    getAllPagesData()
    if (userId) {
      getFavoritePagesData(userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reget])

  useEffect(() => {
    setCurrentPage(allPagesList?.find((item: { pageName: string | undefined }) => item?.pageName === selectedPageKey))
  }, [allPagesList, selectedPageKey])

  useEffect(() => {
    dispatch(setSelectedPageKey(UrlToPageNameMapping[pathName]))
  }, [dispatch, pathName])

  const handleFavoriteConfirmationCancel = () => {
    handleDiscardUnsavedFavoriteChanges()
    setIsFavoriteConfirmationModalOpen(false)
  }

  const isCurrentPageInFavorites = favoritePagesList.map((item: IUserPage) => item?.id).includes(currentPage?.id)

  const handleToggleFavorite = debounce(async () => {
    const payload = {
      userId,
      pageId: currentPage?.id,
    }
    setIsFavoriteIconDisabled(true)
    if (isCurrentPageInFavorites) {
      try {
        const res = await removePageFromFavorites(payload)
        await getFavoritePagesData(userId)
        await getAllPagesData()
        setIsFavoriteIconDisabled(false)
        if (res?.data?.isSuccess) {
          api.success({
            message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.UNSAVE_FAVORITE_PAGE, { Page_Name: selectedPageKey }),
          })
        }
      } catch (err) {
        console.log(err)
      }
    } else {
      try {
        const res = await addPageToFavorites(payload)
        if (res?.data?.isSuccess) {
          api.success({
            message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SAVE_FAVORITE_PAGE, { Page_Name: selectedPageKey }),
          })
        }
        await getFavoritePagesData(userId)
        await getAllPagesData()
        setIsFavoriteIconDisabled(false)
      } catch (err) {
        console.log(err)
      }
    }
  }, 500)

  const handleAccountMenuOpenChange = (newOpen: boolean) => {
    setIsAccountMenuOpen(newOpen)
  }

  const handleDiscardUnsavedFavoriteChanges = () => {
    setFavoritePagesList(initialFavoritePages)
    setAllPagesList(initialAllPagesList)
    setFlag((prev) => !prev)
  }

  const handleSaveFavoriteChanges = async (listItem: any) => {
    setIsLoading(true)
    await updateFavoritePages({
      userId,
      pageIdList: listItem.map((item: any) => item.id),
    })
    setReget((prev) => !prev)
    setIsFavoriteConfirmationModalOpen(false)
    setIsLoading(false)
  }

  const getAllPagesData = async () => {
    try {
      const response = await getAllPages()
      const orderResponse = sortByOrder(response?.data?.result)
      if (response?.data?.result) {
        setAllPagesList(orderResponse)
        setInitialAllPagesList(orderResponse)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const getFavoritePagesData = async (userId: string) => {
    try {
      const response = await getFavoritePages(userId)
      const orderFavoriteList = sortByOrder(response?.data?.result, 'favoritePosition')
      if (response?.data?.result) {
        setFavoritePagesList(orderFavoriteList)
        setInitialFavoritePages(orderFavoriteList)
      }
    } catch (err) {
      console.log(err)
    }
  }
  const deletePages = async () => {
    setIsLoading(true)
    await updateFavoritePages({
      userId,
      pageIdList: [],
    })
    setReget((prev) => !prev)
    setIsFavoriteConfirmationModalOpen(false)
    setIsUnsaveAllFavoritePagesModalOpen(false)
    setIsLoading(false)
  }

  const nonFavoritePagesList = useMemo(() => {
    const favoritePagesIdList = favoritePagesList?.map((favItem: any) => favItem?.id)
    const data = allPagesList?.filter((item: any) => {
      return (
        !favoritePagesIdList?.includes(item?.id) &&
        (policies?.map((policy: any) => policy?.submoduleCodeName)?.includes(item?.submoduleCodeName) ||
          item?.pageName === menuItems.home)
      )
    })

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritePagesList, allPagesList, flag])

  const displayBackButton = ROUTES_WITH_BACK_BUTTON?.includes(pathName as menuUrls)

  const handleGoBack = () => {
    const returnUrl = (location.state as any)?.returnUrl
    if (returnUrl) {
      navigate(returnUrl)
    } else {
      const parentPath = pathName.substring(0, pathName.lastIndexOf('/'))
      navigate(parentPath, { state: location.state })
    }
  }
  
  const onClickHelpIcon = () => {
    const arrays = criticalConfig?.moduleItemList?.map((module: any) => module.submoduleItemList) ?? []
    const empty: any[] = []
    const lastestData = empty?.concat(...arrays)

    const page = lastestData.find((value: any) => value?.pageUrl === pathName)
    if (page) {
      const persistRoot = sessionStorage.getItem('persist:root')
      if (persistRoot) 
        localStorage.setItem(TEMP_SESSION_TRANSFER, persistRoot)
      
      localStorage.setItem(PREVIEW_HELP_PAGE, JSON.stringify({ id: page?.id }))
       window.open(`/help-pages${page?.pageUrl}`, '_blank', 'noreferrer')
    } else {
      window.open(`${menuUrls.pageNotFound}`, '_blank', 'noreferrer')
    }
  }

  const menuActions = [
    {
      key: 1,
      label: (
        <Flex
          className={`${selectedPageKey === menuItems.home ? 'isHome isSelected' : ''} header-icon`}
          onClick={() => {
            navigate(defaultHomePage)
          }}
        >
          <HomeSVG />
        </Flex>
      ),
    },
    {
      key: 2,
      label: isFavoriteIconDisplay && (
        <Popover
          placement='bottomRight'
          content={
            <FavoriteList
              setIsFavoriteConfirmationModalOpen={setIsFavoriteConfirmationModalOpen}
              setIsAccountMenuOpen={setIsAccountMenuOpen}
              favoriteList={favoritePagesList}
            />
          }
          trigger='hover'
        >
          <Flex
            style={{ pointerEvents: isFavoriteIconDisabled ? 'none' : 'auto' }}
            className={`header-icon ${isCurrentPageInFavorites ? 'isFavorite isSelectedFavorite' : ''}`}
            onClick={() => {
              handleToggleFavorite()
            }}
          >
            <FavoriteStarIcon />
          </Flex>
        </Popover>
      ),
    },
    {
      key: 3,
      label: isHelpIconDisplay && (
        <Popover placement='bottom' content='Help'>
          <HelpIcon onClick={onClickHelpIcon} />
        </Popover>
      ),
    },
  ]

  const renderActionBtns = () => {
    if (mode === 'mobile') {
      return (
        <Dropdown
          trigger={['click']}
          menu={{
            items: menuActions,
          }}
        >
          <MinimizeIconSVG className='minimize-icon' onClick={(e) => e.preventDefault()} />
        </Dropdown>
      )
    }

    return (
      <Flex align='center' gap={8}>
        {menuActions.map((item) => item.label)}
      </Flex>
    )
  }

  return (
    <>
      {contextHolder}
      <Header
        className='app-header'
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 500,
          display: 'flex',
          alignItems: 'center',
          height: 'fit-content',
          justifyContent: 'space-between',
          flexDirection: 'row',
          gap: 16,
        }}
      >
        <Flex align='center' gap={2} style={{ flexDirection: 'row', flex: 1 }}>
          {displayBackButton && <LeftOutlined style={{ fontSize: 12 }} onClick={handleGoBack} />}
          <Typography.Text className='header-title'>{selectedPageKey}</Typography.Text>
        </Flex>
        {renderActionBtns()}
        <Popover
          className='header-popover'
          placement='bottomRight'
          content={<AccountMenu />}
          trigger='click'
          open={isAccountMenuOpen}
          onOpenChange={handleAccountMenuOpenChange}
        >
          <Flex className='account-wrap' align='center' justify={'flex-end'} gap={8}>
            <img className='profile-picture' src={ProfilePic} alt='' />
            <Typography.Text className='account-username'>{name}</Typography.Text>
            <ArrowDownIcon />
          </Flex>
        </Popover>
        <DNDComp
          favoriteList={favoritePagesList}
          nonFavoriteList={nonFavoritePagesList}
          setFavoriteList={setFavoritePagesList}
          handleSaveFavoriteChanges={handleSaveFavoriteChanges}
          isFavoriteConfirmationModalOpen={isFavoriteConfirmationModalOpen}
          handleFavoriteConfirmationCancel={handleFavoriteConfirmationCancel}
          handleDiscardUnsavedFavoriteChanges={handleDiscardUnsavedFavoriteChanges}
          setIsFavoriteConfirmationModalOpen={setIsFavoriteConfirmationModalOpen}
          setIsUnsaveAllFavoritePagesModalOpen={setIsUnsaveAllFavoritePagesModalOpen}
          isLoading={isLoading}
        />

        <Modal
          open={isUnsaveAllFavoritePagesModalOpen}
          cancelButtonProps={{ disabled: false }}
          onCancel={() => {
            handleDiscardUnsavedFavoriteChanges()
            setIsUnsaveAllFavoritePagesModalOpen(false)
          }}
          centered
          width={480}
          footer={false}
        >
          <Flex vertical align='center' gap={12}>
            <DangerIcon />
            <Typography.Title style={{ fontSize: '20px', fontWeight: '700', margin: '0', padding: '0' }}>
              {t('OverallFunctionality:unsave_all_modal_title_text')}
            </Typography.Title>
            <Typography.Text style={{ fontSize: '14px', color: colorTextSubtle }}>
              {t('OverallFunctionality:unsave_all_modal_description_text')}
            </Typography.Text>
            <Space style={{ paddingTop: '16px' }} size={16}>
              <Button
                key='logout-modal-cancel'
                type='default'
                style={{ width: '80px', fontWeight: 600 }}
                onClick={() => {
                  handleDiscardUnsavedFavoriteChanges()
                  setIsUnsaveAllFavoritePagesModalOpen(false)
                }}
                ghost
              >
                {t('OverallFunctionality:no_btn')}
              </Button>
              <Button style={{ width: '80px' }} type='primary' onClick={deletePages} disabled={isLoading}>
                {t('OverallFunctionality:yes_btn')}
              </Button>
            </Space>
          </Flex>
        </Modal>
      </Header>
    </>
  )
}

export default AppHeader
